import { constructInterval, constructIntervalForAPI } from "@/common/intervals";
import {
  childTypeAllowed,
  getChildType,
  getMaxN,
  getParentType,
} from "@/common/intervalTypes";
import { getName } from "@/common/names";
import { HGInterval, HGIntervalAPI, HGIntervalType } from "@/common/types";
import { MapInterval } from "@/engine/engine";
import { getNow } from "@/main";

/**
 * Which edge of a stack's covered span stays fixed when its level changes.
 * A range's start anchors to "begin", its end to "end". See HGStack.push().
 */
export type HGAnchor = "begin" | "end";

export class HGStack {
  #interval: HGInterval;
  #intervalAPI: HGIntervalAPI;
  #parentIts: string[];
  #beginSecond: bigint;
  #endSecond: bigint;
  #min?: bigint;
  #max?: bigint;
  #name: string;

  constructor(n: bigint, it: string, parentIts: string[] = []) {
    this.#interval = { n, it };
    this.#intervalAPI = constructIntervalForAPI(this.#interval);
    this.#parentIts = parentIts;
    this.#beginSecond = 0n;
    this.#endSecond = 0n;
    this.#name = "Loading...";
    if (n) {
      this.#updateInterval(n, it);
    }
  }

  // Read methods

  getName() {
    return this.#name;
  }

  getInterval() {
    return this.#interval;
  }

  getIntervalAPI() {
    return this.#intervalAPI;
  }

  getParentIts() {
    return this.#parentIts;
  }

  // undefined when the stack sits at the top of the hierarchy ("byr"):
  // there is no parent level above it.
  getParentIt(): HGIntervalType | undefined {
    return this.#parentIts[this.#parentIts.length - 1];
  }

  getBeginSecond() {
    return this.#beginSecond;
  }

  getEndSecond() {
    return this.#endSecond;
  }

  nPossible(n: bigint) {
    if (n == 0n) return false;

    // Bounded types (the top one) can't be mapped by the engine past their
    // limit, so those n are not navigable at all.
    const maxN = getMaxN(this.#interval.it);
    if (maxN !== undefined && (n > maxN || n < -maxN)) return false;

    if (this.#max === undefined && this.#min === undefined) return true;

    const possible =
      (this.#min === undefined || n >= this.#min) &&
      (this.#max === undefined || this.#max >= n);

    return possible;
  }

  // Modify methods

  // Which edge of the covered span a level change keeps fixed. A stack used as
  // a range's start anchors to "begin" (drilling into it must not move the
  // range's first second); one used as the end anchors to "end". Getting this
  // wrong inverts ranges: drilling an end from year 2025 into months with a
  // "begin" anchor lands on January, before a start of, say, December 2025.
  // With the right anchor, push/pop can only ever refine or widen a range,
  // never move it — which is why the ordering check is needed in exactly one
  // place (HGStacks.setFocusedInterval) and nowhere else.
  //
  // #min/#max are deliberately NOT consulted here: they are expressed in units
  // of the *current* it and become meaningless the moment it changes. Callers
  // clear them first and HGStacks.#updateMinMax() re-derives them (in seconds,
  // so level-agnostically) once the move is done.

  // Returns false when there is nothing to drill into (the stack is at the
  // bottom of the hierarchy, "s"), so callers can skip notifying listeners.
  push(it?: HGIntervalType, anchor: HGAnchor = "begin") {
    if (!it) {
      it = getChildType(this.#interval.it);
    }
    if (!it) return false;

    const childSpan = MapInterval(this.getInterval(), it);

    let newN = anchor === "end" ? childSpan.end.n : childSpan.begin.n;

    // Only jump to 'now' when the stack is currently at 'now'
    // (avoid forcing now when user is browsing historical positions).
    // Never for an end anchor: snapping the end of a range to the current
    // hour would silently shorten what the user selected.
    if (anchor === "begin") {
      const nowObj = getNow();
      const nowForIt = nowObj[it];
      const nowForCurrentIt = nowObj[this.#interval.it];
      if (nowForIt && this.#interval.n === nowForCurrentIt) {
        newN = nowForIt;
      }
    }

    this.#parentIts.push(this.#interval.it);
    this.#updateInterval(newN, it);
    return true;
  }

  // The exact inverse of push(): it walks #parentIts rather than
  // getParentType(), so it retraces the non-canonical hops push() can make
  // (c -> y skips dec, because c.defaultChildType isn't in c.childrenTypes).
  // Returns false at the top of the hierarchy, mirroring push().
  pop(anchor: HGAnchor = "begin") {
    const it = this.#parentIts[this.#parentIts.length - 1];
    if (!it) return false;

    const parentSpan = MapInterval(this.getInterval(), it);
    // Only pop once the mapping succeeded, so a throw leaves the chain intact.
    this.#parentIts.pop();
    this.#updateInterval(
      anchor === "end" ? parentSpan.end.n : parentSpan.begin.n,
      it,
    );
    return true;
  }

  // Returns false when the neighbour isn't reachable (an end of a bounded
  // type, or the min/max of a selected range), leaving the stack untouched.
  shift(n: number) {
    const newN = this.#calculateNewN(n);
    if (!this.nPossible(newN)) return false;
    this.#updateInterval(newN, this.#interval.it);
    return true;
  }

  setInterval(interval: HGInterval) {
    if (interval.it == this.#interval.it) {
      if (!this.nPossible(interval.n)) {
        throw Error("Set interval impossible");
      }
      this.#updateInterval(interval.n, interval.it);
      return;
    }

    const index = this.#parentIts.indexOf(interval.it);
    if (index >= 0) {
      let toPop = this.#parentIts.length - index;
      while (toPop--) {
        this.#parentIts.pop();
      }
      this.#updateInterval(interval.n, interval.it);
      return;
    } else {
      for (let i = this.#parentIts.length - 1; i >= 0; --i) {
        if (childTypeAllowed(this.#parentIts[i], interval.it)) {
          this.#parentIts = this.#parentIts.slice(0, i + 1);
          this.#updateInterval(interval.n, interval.it);
          return;
        }
      }

      // Drill down from the deepest known parent looking for a level that
      // accepts interval.it. Work on a copy so a failed search doesn't leave
      // the stack with a bogus parent chain.
      const parentIts = this.#parentIts.slice();
      if (parentIts.length === 0) {
        // Stack is at the top of the hierarchy, so its own type is the only
        // level the search can start from.
        if (childTypeAllowed(this.#interval.it, interval.it)) {
          this.#parentIts = [this.#interval.it];
          this.#updateInterval(interval.n, interval.it);
          return;
        }
        parentIts.push(this.#interval.it);
      }
      while (true) {
        const lastParentIt = parentIts[parentIts.length - 1];
        const nextParentIt = getChildType(lastParentIt);
        if (nextParentIt) {
          parentIts.push(nextParentIt);
          if (childTypeAllowed(nextParentIt, interval.it)) {
            this.#parentIts = parentIts;
            this.#updateInterval(interval.n, interval.it);
            return;
          }
        } else {
          break;
        }
      }

      throw Error(`Can't set interval, ${interval}, ${this.#parentIts}`);
    }
  }

  setMaxBeginSecond(maxSecond?: bigint) {
    if (maxSecond) {
      const span = MapInterval(
        constructInterval(maxSecond, "s"),
        this.#interval.it,
      );
      this.#max = span.begin.n;
      if (this.#interval.n > this.#max) {
        this.#updateInterval(this.#max, this.#interval.it);
      }
    } else if (this.#max !== undefined) {
      this.#max = undefined;
    }
  }

  setMinEndSecond(minSecond?: bigint) {
    if (minSecond) {
      const span = MapInterval(
        constructInterval(minSecond, "s"),
        this.#interval.it,
      );
      this.#min = span.begin.n;
      if (this.#interval.n < this.#min) {
        this.#updateInterval(this.#min, this.#interval.it);
      }
    } else if (this.#min !== undefined) {
      this.#min = undefined;
    }
  }

  // Misc. methods

  #calculateNewN(shiftN: number) {
    let newN = this.#interval.n + BigInt(shiftN);
    if (newN <= 0n && this.#interval.n > 0n) {
      newN -= 1n;
    } else if (newN >= 0n && this.#interval.n < 0n) {
      newN += 1n;
    }
    return newN;
  }

  #updateInterval(n: bigint, it: string) {
    this.#interval = { n, it };
    this.#intervalAPI = constructIntervalForAPI(this.#interval);
    this.#name = getName(this.#interval);
    const seconds = MapInterval(this.#interval, "s");
    this.#beginSecond = seconds.begin.n;
    this.#endSecond = seconds.end.n;
  }
}

export function createStack() {
  const now = getNow();
  return new HGStack(now["d"], "d", [
    "byr",
    "myr",
    "kyr",
    "c",
    "dec",
    "y",
    "m",
  ]);
}

export function generateStack(interval: HGInterval) {
  const parentIts = [];
  let parentIt = getParentType(interval.it);
  while (parentIt) {
    parentIts.push(parentIt);
    parentIt = getParentType(parentIt);
  }

  return new HGStack(interval.n, interval.it, parentIts.reverse());
}

export function copyStack(sourceStack: HGStack) {
  const interval = sourceStack.getInterval();
  return new HGStack(
    interval.n,
    interval.it,
    sourceStack.getParentIts().slice(),
  );
}
